import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { db } from '../../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { Loader2, ArrowRight, BookOpen, Star, GraduationCap, AlignLeft, User, MessageCircle, ShieldCheck, Share2, Copy, CheckCircle2, Edit2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import CourseCard from '../../components/dashboard/CourseCard';
import ReviewDisplay from '../../components/reviews/ReviewDisplay';
import ReviewSubmissionModal from '../../components/reviews/ReviewSubmissionModal';
import InstructorReviewBadge from '../../components/reviews/InstructorReviewBadge';
import { fetchReviewsForTarget, calculateReviewStats } from '../../services/reviewService';
import DOMPurify from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faXTwitter, faLinkedinIn, faInstagram, faFacebookF, faYoutube, faBehance } from '@fortawesome/free-brands-svg-icons';
import InstructorProfileSkeleton from '../../components/skeletons/InstructorProfileSkeleton';
export default function InstructorPublicProfile() {
  const { usernameOrId } = useParams();
  const { currentUser } = useAuth();
  const [instructor, setInstructor] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [averageRating, setAverageRating] = useState(0);
  const [showShareToast, setShowShareToast] = useState(false);

  // Review specific states
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isEligibleToReview, setIsEligibleToReview] = useState(false);
  const [hasExistingReview, setHasExistingReview] = useState(false);

  // Listen for edit events from cards
  useEffect(() => {
    const handleOpenEdit = (e) => {
      if (e.detail.targetId === instructor?.id) {
        setIsReviewModalOpen(true);
      }
    };
    window.addEventListener('OPEN_REVIEW_EDIT', handleOpenEdit);
    return () => window.removeEventListener('OPEN_REVIEW_EDIT', handleOpenEdit);
  }, [instructor]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        let docSnap;
        const usernameQ = query(
          collection(db, 'users'),
          where('role', '==', 'instructor'),
          where('username', '==', usernameOrId)
        );
        const qSnap = await getDocs(usernameQ);

        if (!qSnap.empty) {
          docSnap = qSnap.docs[0];
        } else {
          docSnap = await getDoc(doc(db, 'users', usernameOrId));
        }

        if (docSnap && docSnap.exists() && docSnap.data().role === 'instructor') {
          setInstructor({ id: docSnap.id, ...docSnap.data() });
        } else {
          setInstructor(null);
          setLoading(false);
          return;
        }

        const actualId = docSnap.id;

        // Fetch published courses for this instructor
        const coursesQ = query(
          collection(db, 'courses'),
          where('instructorId', '==', actualId),
          where('status', '==', 'published')
        );
        const coursesSnap = await getDocs(coursesQ);
        const fetchedCourses = coursesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setCourses(fetchedCourses);

        // Calculate actual instructor rating from their direct reviews
        const instructorReviews = await fetchReviewsForTarget(actualId);
        const stats = calculateReviewStats(instructorReviews);
        setAverageRating(stats.average);

        // Check if current user is enrolled in any of these courses
        if (currentUser && fetchedCourses.length > 0) {
          const enrollmentsQ = query(
            collection(db, 'enrollments'),
            where('userId', '==', currentUser.uid)
          );
          const enrollmentsSnap = await getDocs(enrollmentsQ);
          const enrolledCourseIds = enrollmentsSnap.docs.map(d => d.data().courseId);

          const hasCommonCourse = fetchedCourses.some(course => enrolledCourseIds.includes(course.id));
          setIsEligibleToReview(hasCommonCourse);

          // Check if already reviewed
          const existingReview = instructorReviews.find(r => r.userId === currentUser.uid);
          if (existingReview) setHasExistingReview(true);
        }

      } catch (err) {
        console.error("Error fetching instructor profile:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [usernameOrId, currentUser]);

  if (loading) {
    return <InstructorProfileSkeleton />;
  }

  if (!instructor) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-24 pb-12 bg-slate-50 dark:bg-zinc-950 text-slate-500">
        <User className="w-16 h-16 mb-4 text-slate-300 dark:text-zinc-700" />
        <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">المدرب غير موجود</h2>
        <p className="font-medium text-lg mb-6">عذراً، لم نتمكن من العثور على الصفحة المطلوبة.</p>
        <Link to="/" className="px-6 py-3 bg-primary-600 text-white font-bold rounded-xl">العودة للرئيسية</Link>
      </div>
    );
  }

  // Share profile handler
  const handleShareProfile = async () => {
    const shareData = {
      title: `${instructor.name} — مدرب في لُباب`,
      text: `تعرف على المدرب ${instructor.name} في منصة لُباب التعليمية`,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setShowShareToast(true);
        setTimeout(() => setShowShareToast(false), 2500);
      }
    } catch {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 2500);
    }
  };

  const socialKeys = ['whatsapp', 'twitter', 'linkedin', 'instagram', 'facebook', 'youtube', 'behance'];
  const socialConfig = {
    whatsapp: faWhatsapp,
    twitter: faXTwitter,
    linkedin: faLinkedinIn,
    instagram: faInstagram,
    facebook: faFacebookF,
    youtube: faYoutube,
    behance: faBehance
  };
  const activeSocials = socialKeys.filter(key => instructor.instructorData?.social?.[key]);

  const profileUrl = `https://lobabschool.com/instructor/${instructor.username || usernameOrId}`;
  const bioPlainText = (instructor.instructorData?.bio || '').replace(/<[^>]*>/g, '');

  // Collect active social links for sameAs
  const sameAsLinks = socialKeys
    .filter(key => instructor.instructorData?.social?.[key])
    .map(key => {
      const rawUrl = instructor.instructorData.social[key];
      if (key === 'whatsapp' && !rawUrl.startsWith('http')) return `https://wa.me/${rawUrl.replace(/[^0-9+]/g, '')}`;
      return rawUrl;
    });

  return (
    <div className="min-h-screen flex flex-col pt-24 pb-16 bg-slate-50 dark:bg-zinc-950 transition-colors">
      {/* SEO: Dynamic Meta Tags, Open Graph & JSON-LD */}
      <Helmet>
        <title>{`${instructor.name} — مدرب في لُباب`}</title>
        <meta name="description" content={bioPlainText.slice(0, 160) || `${instructor.name} — مدرب في منصة لُباب التعليمية`} />
        <link rel="canonical" href={profileUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={`${instructor.name} — مدرب في لُباب`} />
        <meta property="og:description" content={bioPlainText.slice(0, 200) || `${instructor.name} — مدرب في منصة لُباب التعليمية`} />
        <meta property="og:url" content={profileUrl} />
        {instructor.photoURL && <meta property="og:image" content={instructor.photoURL} />}
        <meta property="og:site_name" content="Lobab School | لُباب" />
        <meta property="og:locale" content="ar_SA" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${instructor.name} — مدرب في لُباب`} />
        <meta name="twitter:description" content={bioPlainText.slice(0, 200) || `${instructor.name} — مدرب في منصة لُباب التعليمية`} />
        {instructor.photoURL && <meta name="twitter:image" content={instructor.photoURL} />}

        {/* Schema.org Person JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: instructor.name,
            url: profileUrl,
            ...(instructor.photoURL && { image: instructor.photoURL }),
            ...(instructor.instructorData?.specialization && {
              jobTitle: instructor.instructorData.specialization,
            }),
            ...(bioPlainText && { description: bioPlainText }),
            affiliation: {
              '@type': 'Organization',
              name: 'Lobab School | لُباب',
              url: 'https://lobabschool.com',
            },
            ...(sameAsLinks.length > 0 && { sameAs: sameAsLinks }),
          })}
        </script>
      </Helmet>

      {/* Share Toast */}
      {showShareToast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[100] animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-xl shadow-2xl font-bold text-sm">
            <CheckCircle2 className="w-5 h-5" /> تم نسخ رابط الملف الشخصي!
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 max-w-6xl animate-in fade-in duration-700 space-y-10 relative">

        {/* Back Link */}
        <Link to="/courses" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:hover:text-white font-medium transition-colors mb-4 rtl:flex-row-reverse relative z-20">
          تصفح الدورات <ArrowRight className="w-4 h-4 ml-2 rtl:mr-2 rtl:ml-0 rtl:rotate-180" />
        </Link>
        <div className="clear-both hidden"></div>

        {/* Premium Profile Header Card */}
        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/5 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden group">
          {/* Ambient Glows */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 dark:bg-amber-500/5 rounded-full blur-[80px] pointer-events-none translate-x-1/2 -translate-y-1/2 group-hover:scale-150 transition-transform duration-1000"></div>

          <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-10">
            {/* Avatar */}
            <div className="w-40 h-40 md:w-48 md:h-48 rounded-3xl overflow-hidden border-8 border-slate-50 dark:border-zinc-800 shadow-xl shrink-0 group-hover:scale-105 transition-transform duration-500 bg-slate-100 dark:bg-zinc-800 flex items-center justify-center">
              {instructor.photoURL ? (
                <img src={instructor.photoURL} alt={instructor.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-20 h-20 text-slate-300 dark:text-zinc-600" />
              )}
            </div>

            {/* Title & Info */}
            <div className="flex-1 text-center md:text-right w-full">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                <ShieldCheck className="w-10 h-10 text-amber-500 hidden sm:block" />
                <h1 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight">{instructor.name}</h1>
              </div>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-slate-500 dark:text-zinc-400 font-bold text-lg mb-6">
                <span className="flex items-center gap-1">
                  <GraduationCap className="w-5 h-5 text-indigo-500" />
                  {instructor.instructorData?.specialization || 'طاقم التدريس'}
                </span>
                <span className="w-1.5 h-1.5 bg-slate-300 dark:bg-zinc-700 rounded-full hidden sm:block"></span>
                <InstructorReviewBadge instructorId={instructor.id} className="text-xl" />
              </div>

              {/* Bio Block */}
              <div className="bg-slate-50 dark:bg-black/20 rounded-2xl p-6 border border-slate-100 dark:border-white/5 relative mb-6">
                <AlignLeft className="absolute top-4 right-4 w-6 h-6 text-slate-200 dark:text-zinc-800 pointer-events-none" />
                <p
                  className="text-slate-600 dark:text-zinc-300 leading-relaxed text-lg font-medium pr-8"
                  dangerouslySetInnerHTML={{ __html: instructor.instructorData?.bio ? DOMPurify.sanitize(instructor.instructorData.bio) : 'لا توجد نبذة حالياً...' }}
                />
              </div>

              {/* Social Icons + Share */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                {activeSocials.map(key => {
                  const rawUrl = instructor.instructorData.social[key];
                  // Automatically format whatsapp numbers if they don't have http/https
                  const finalUrl = (key === 'whatsapp' && !rawUrl.startsWith('http'))
                    ? `https://wa.me/${rawUrl.replace(/[^0-9+]/g, '')}`
                    : rawUrl;

                  return (
                    <a
                      key={key}
                      href={finalUrl}
                      target="_blank"
                      rel="noreferrer"
                      title={key}
                      className="text-slate-500 dark:text-zinc-400 hover:text-amber-500 dark:hover:text-amber-400 hover:scale-110 hover:shadow-[0_0_20px_rgba(218,165,32,0.3)] transition-all duration-300 bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-full flex items-center justify-center w-11 h-11 shrink-0"
                    >
                      <FontAwesomeIcon icon={socialConfig[key]} className="w-5 h-5" />
                    </a>
                  );
                })}

                {activeSocials.length > 0 && (
                  <div className="w-px h-8 bg-slate-200 dark:bg-white/10 mx-1"></div>
                )}

                <button
                  onClick={handleShareProfile}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-500 dark:text-zinc-400 hover:text-amber-600 dark:hover:text-amber-400 hover:border-amber-200 dark:hover:border-amber-500/20 transition-all font-bold text-sm"
                >
                  <Share2 className="w-4 h-4" /> مشاركة الملف الشخصي
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Courses Grid Label */}
        <div className="flex items-center gap-3 pt-8 border-t border-slate-200 dark:border-white/5 relative z-10 w-full mb-6">
          <BookOpen className="w-7 h-7 text-primary-500" />
          <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white">أحدث دورات المدرب</h2>
        </div>

        {courses.length === 0 ? (
          <div className="text-center py-20 bg-white/50 dark:bg-zinc-900/40 rounded-3xl border border-slate-200/50 dark:border-white/5">
            <p className="text-xl font-bold text-slate-500 dark:text-zinc-400">لا توجد دورات منشورة حالياً لهذا المدرب.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 relative z-10 w-full">
            {courses.map(course => (
              <CourseCard
                key={course.id}
                course={course}
                isEnrolled={false}
                isPublicView={true}
              />
            ))}
          </div>
        )}

        {/* Reviews Section - Gated */}
        <div className="pt-12 mt-12 border-t border-slate-200 dark:border-white/5 relative z-10 w-full mb-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <Star className="w-7 h-7 text-amber-500" />
              <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white">آراء الطلاب</h2>
            </div>

            {isEligibleToReview && (
              <button
                onClick={() => setIsReviewModalOpen(true)}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-slate-900 hover:bg-black dark:bg-white/10 dark:hover:bg-white/20 text-white font-bold rounded-xl transition-all shadow-md w-full sm:w-auto"
              >
                {hasExistingReview ? (
                  <>
                    <Edit2 className="w-5 h-5" />
                    تعديل تقييمك للمدرب
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-5 h-5" />
                    إضافة تقييم للمدرب
                  </>
                )}
              </button>
            )}

            {(!isEligibleToReview && currentUser) && (
              <div className="px-5 py-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-amber-600 dark:text-amber-400 font-bold rounded-xl text-sm">
                متاح فقط للطلاب المسجلين في إحدى دورات المدرب
              </div>
            )}
          </div>

          <ReviewDisplay targetType="instructor" targetId={instructor.id} />
        </div>

      </div>

      {/* Gated Review Modal */}
      {currentUser && (
        <ReviewSubmissionModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          user={currentUser}
          targetType="instructor"
          targetId={instructor.id}
          onSuccess={() => window.location.reload()}
        />
      )}
    </div>
  );
}
